import { Badge } from '@fluentui/react-components';

// Renders a wave BPRT lifecycle status (ADR-021 / FD-135) as a Fluent Badge.
// `null`/empty means the wave was never activated. Status strings are normalized
// to lowercase before matching so casing variance lands on the same colour.
//   active      → success (green)  — a live BPRT is minted for the wave
//   expired     → warning (amber)  — needs an operator-assisted re-mint
//   revoked     → subtle  (zinc)   — intentional end state (wave completed)
//   notactivated/none/null → subtle (zinc) "Not activated"
//   anything else → warning (amber) — see below
//
// FD-388 consolidated a second implementation into this one. `WavePanel` carried
// its own `WaveStatusBadge` rendering this same `bprtStatus` field with DIFFERENT
// colours (`revoked` and never-activated both informative-blue), while the five
// other surfaces used this component — one vocabulary with two answers, and the
// wave lifecycle panel was the one showing the minority answer.
//
// The merge kept this component and took the *other* one's unknown-value
// fallback, which was the better of the two: an unrecognised lifecycle value
// renders `warning`, not `subtle`. That is the FD-197 rule — a real status value
// must never fall through to a neutral grey chip, because a chip that reads as
// "nothing to report" is exactly how a faulted device looked benign in the
// device grid. An unrecognised value is a thing to see.
export default function BprtStatusBadge({ status }: { status: string | null | undefined }) {
  const normalized = (status ?? '').trim().toLowerCase();

  if (normalized === '' || normalized === 'notactivated' || normalized === 'none') {
    return (
      <Badge appearance="tint" color="subtle">
        Not activated
      </Badge>
    );
  }
  if (normalized === 'active') {
    return (
      <Badge appearance="tint" color="success">
        Active
      </Badge>
    );
  }
  if (normalized === 'expired') {
    return (
      <Badge appearance="tint" color="warning">
        Expired
      </Badge>
    );
  }
  if (normalized === 'revoked') {
    return (
      <Badge appearance="tint" color="subtle">
        Revoked
      </Badge>
    );
  }
  // Unknown/other lifecycle value — surface it verbatim, and in amber rather
  // than grey: we do not recognise it, so an operator should look (FD-197).
  return (
    <Badge appearance="tint" color="warning">
      {status}
    </Badge>
  );
}
